/**
 * HMAC-SHA256 工具
 *
 * 用途：
 * 1. 客户端 → verify-worker 请求签名（用 app_secret 对请求体 + 时间戳签名）
 * 2. Worker 间内部调用签名（用 INTERNAL_HMAC_KEY 对 timestamp 签名）
 * 3. JWT 签名（HS256）
 */

const encoder = new TextEncoder();

/** 计算 HMAC-SHA256，返回 ArrayBuffer */
export async function hmacSha256Buffer(
  key: string,
  message: string,
): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(key) as BufferSource,
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  return crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message) as BufferSource);
}

/** 计算 HMAC-SHA256，返回 hex */
export async function hmacSha256Hex(key: string, message: string): Promise<string> {
  const buf = await hmacSha256Buffer(key, message);
  return bufToHex(buf);
}

/** 比对两个 hex 字符串是否相等（常量时间） */
export function safeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/** ArrayBuffer → hex */
function bufToHex(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * 构造客户端请求待签名串
 *
 * 规则：method\npath\ntimestamp\nbody（body 为原始 JSON 字符串）
 */
export function buildSignPayload(
  method: string,
  path: string,
  timestamp: number,
  body: string,
): string {
  return `${method.toUpperCase()}\n${path}\n${timestamp}\n${body}`;
}

/**
 * 计算客户端请求签名
 *
 * @param appSecret 应用密钥
 * @param method HTTP 方法
 * @param path 请求路径（不含 query）
 * @param timestamp 时间戳（秒）
 * @param body 原始请求体字符串
 */
export async function signClientRequest(
  appSecret: string,
  method: string,
  path: string,
  timestamp: number,
  body: string,
): Promise<string> {
  const payload = buildSignPayload(method, path, timestamp, body);
  return hmacSha256Hex(appSecret, payload);
}

/**
 * 校验客户端请求签名
 */
export async function verifyClientSignature(
  appSecret: string,
  method: string,
  path: string,
  timestamp: number,
  body: string,
  signature: string,
): Promise<boolean> {
  const expected = await signClientRequest(appSecret, method, path, timestamp, body);
  return safeEqualHex(expected, signature);
}

/**
 * 计算内部调用签名（Worker 间服务绑定）
 *
 * 规则：timestamp + caller
 */
export async function signInternalCall(
  internalHmacKey: string,
  caller: string,
  timestamp: number,
): Promise<string> {
  return hmacSha256Hex(internalHmacKey, `${caller}:${timestamp}`);
}

/**
 * 校验内部调用签名
 */
export async function verifyInternalSignature(
  internalHmacKey: string,
  caller: string,
  timestamp: number,
  signature: string,
): Promise<boolean> {
  const expected = await signInternalCall(internalHmacKey, caller, timestamp);
  return safeEqualHex(expected, signature);
}
